import {
  AlertCircle,
  Camera,
  CheckCircle2,
  FileText,
  Film,
  Images,
  Link2Off,
  RotateCw,
} from 'lucide-react';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type ReactNode,
} from 'react';
import { useParams } from 'react-router-dom';

import {
  DEAD_LINK_MESSAGE,
  MAX_MESSAGE_LENGTH,
  createQueueItem,
  decideAfterFailure,
  formatBytes,
  holdFileBytes,
  manualRetry,
  markDone,
  markProgress,
  markUnreadable,
  markUploading,
  newestFirst,
  parseRetryAfter,
  selectNext,
  summarise,
  uuidV4,
  type QueueItem,
  type SentFile,
  type UploadOutcome,
} from './upload-queue.logic';

import { env } from '@/lib/env';

/**
 * The page a CLIENT opens from a link, with no account and no app (spec §2.4).
 *
 * Deliberately outside the authenticated router: the token in the URL IS the
 * credential, it is scoped to one client, and it is upload-only. The heading
 * does not name the client or the agency: naming them would mean a public
 * endpoint had to disclose them, and an unauthenticated page should not be
 * able to confirm which client a leaked token belongs to. A revoked, unknown
 * or malformed token all answer the same 404, so this page shows one message.
 *
 * Files upload as soon as they are picked, one at a time, over XHR so each
 * row shows real progress. Every file carries an `uploadId` reused on retry
 * (R.7, so a lost 201 never makes a duplicate) and every page visit a
 * `batchId` that groups the files with the optional message (R.10). The
 * retry rules — 429 backoff, one network retry, 409 new batch — live in
 * `upload-queue.logic.ts` and are unit tested there.
 */

function newId(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  return uuidV4((b) => crypto.getRandomValues(b));
}

function readMessage(xhr: XMLHttpRequest): string | null {
  try {
    const body = JSON.parse(xhr.responseText) as { message?: unknown };
    return typeof body.message === 'string' ? body.message : null;
  } catch {
    return null;
  }
}

type LinkState = 'checking' | 'live' | 'dead';
type MessageState = 'idle' | 'saving' | 'saved' | 'error';

const HISTORY_REFRESH_DEBOUNCE_MS = 1500;

export function ClientUploadPage(): ReactNode {
  const { token } = useParams<{ token: string }>();
  const baseUrl =
    token === undefined ? null : `${env.VITE_API_URL}/api/agency/intake/upload/${token}`;

  const [linkState, setLinkState] = useState<LinkState>(token === undefined ? 'dead' : 'checking');
  const [items, setItems] = useState<QueueItem[]>([]);
  const [sent, setSent] = useState<SentFile[]>([]);
  const [tick, setTick] = useState(0);
  const [message, setMessage] = useState('');
  const [savedMessage, setSavedMessage] = useState('');
  const [messageState, setMessageState] = useState<MessageState>('idle');

  const batchIdRef = useRef<string>(newId());
  const filesRef = useRef(new Map<string, File>());
  const thumbsRef = useRef(new Map<string, string>());
  const activeXhrRef = useRef<XMLHttpRequest | null>(null);
  const activeIdRef = useRef<string | null>(null);
  const historyTimerRef = useRef<number | null>(null);
  const mountedRef = useRef(true);

  const photosInput = useRef<HTMLInputElement>(null);
  const cameraInput = useRef<HTMLInputElement>(null);
  const filesInput = useRef<HTMLInputElement>(null);

  // ---- history -------------------------------------------------------------

  const loadHistory = useCallback(async (): Promise<void> => {
    if (baseUrl === null) return;
    try {
      const res = await fetch(baseUrl, { cache: 'no-store' });
      if (!mountedRef.current) return;
      if (res.status === 404 || res.status === 410) {
        setLinkState('dead');
        return;
      }
      if (!res.ok) {
        // A 429 or a blip on history must never hide the upload buttons.
        setLinkState((s) => (s === 'checking' ? 'live' : s));
        return;
      }
      const body = (await res.json()) as { files?: SentFile[] };
      if (!mountedRef.current) return;
      setSent(newestFirst(body.files ?? []));
      setLinkState((s) => (s === 'checking' ? 'live' : s));
    } catch {
      // History is a convenience; the page's job is to accept files.
      if (mountedRef.current) setLinkState((s) => (s === 'checking' ? 'live' : s));
    }
  }, [baseUrl]);

  const scheduleHistoryRefresh = useCallback((): void => {
    if (historyTimerRef.current !== null) window.clearTimeout(historyTimerRef.current);
    historyTimerRef.current = window.setTimeout(() => {
      historyTimerRef.current = null;
      void loadHistory();
    }, HISTORY_REFRESH_DEBOUNCE_MS);
  }, [loadHistory]);

  useEffect(() => {
    void loadHistory();
  }, [loadHistory]);

  // ---- lifecycle -----------------------------------------------------------

  useEffect(() => {
    mountedRef.current = true;
    const thumbs = thumbsRef.current;
    return () => {
      mountedRef.current = false;
      activeXhrRef.current?.abort();
      if (historyTimerRef.current !== null) window.clearTimeout(historyTimerRef.current);
      for (const url of thumbs.values()) URL.revokeObjectURL(url);
      thumbs.clear();
    };
  }, []);

  const itemsRef = useRef<QueueItem[]>(items);
  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  const summary = useMemo(() => summarise(items), [items]);

  useEffect(() => {
    if (!summary.busy) return;
    const onBeforeUnload = (e: BeforeUnloadEvent): void => {
      e.preventDefault();
      // Older browsers need a returnValue to show the prompt.
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [summary.busy]);

  // ---- queue driver --------------------------------------------------------

  const updateItem = useCallback((id: string, fn: (it: QueueItem) => QueueItem): void => {
    setItems((prev) => prev.map((it) => (it.id === id ? fn(it) : it)));
  }, []);

  const startUpload = useCallback(
    (item: QueueItem): void => {
      const file = filesRef.current.get(item.id);
      if (baseUrl === null || file === undefined || item.mime === null) return;

      activeIdRef.current = item.id;
      updateItem(item.id, markUploading);

      const form = new FormData();
      // Fields before the file: multer reads them in wire order.
      form.append('uploadId', item.id);
      form.append('batchId', batchIdRef.current);
      // Re-label the part when the browser gave no type (HEIC/MOV on some
      // phones); the server checks the part's type, not the extension.
      const body = file.type === item.mime ? file : file.slice(0, file.size, item.mime);
      form.append('file', body, file.name);

      const xhr = new XMLHttpRequest();
      activeXhrRef.current = xhr;
      xhr.open('POST', baseUrl);

      const finish = (outcome: UploadOutcome | 'ok'): void => {
        activeXhrRef.current = null;
        activeIdRef.current = null;
        if (!mountedRef.current) return;
        if (outcome === 'ok') {
          updateItem(item.id, markDone);
          scheduleHistoryRefresh();
          return;
        }
        // Decide from the latest committed row, outside any state updater, so
        // the side effects below run exactly once (updaters may be deferred
        // or, under StrictMode, run twice).
        const current = itemsRef.current.find((it) => it.id === item.id) ?? item;
        const decision = decideAfterFailure(current, outcome, Date.now());
        let next = decision.item;

        if (decision.effect === 'dead-link') {
          setLinkState('dead');
          // Nothing else can succeed on a dead link.
          setItems((prev) =>
            prev.map((it) =>
              it.id === item.id
                ? next
                : it.status === 'pending' || it.status === 'uploading'
                  ? { ...it, status: 'failed', reason: DEAD_LINK_MESSAGE, retryable: false }
                  : it,
            ),
          );
          return;
        }
        if (decision.effect === 'new-batch') {
          batchIdRef.current = newId();
          // The message belonged to the old batch; save it again on the new one.
          setSavedMessage('');
        }
        if (decision.effect === 'new-upload-id') {
          const replacementId = newId();
          const f = filesRef.current.get(item.id);
          const thumb = thumbsRef.current.get(item.id);
          if (f !== undefined) filesRef.current.set(replacementId, f);
          if (thumb !== undefined) thumbsRef.current.set(replacementId, thumb);
          next = { ...next, id: replacementId };
        }
        setItems((prev) => prev.map((it) => (it.id === item.id ? next : it)));
      };

      xhr.upload.onprogress = (e): void => {
        if (!e.lengthComputable || !mountedRef.current) return;
        updateItem(item.id, (it) => markProgress(it, e.loaded, e.total));
      };
      xhr.onload = (): void => {
        if (xhr.status >= 200 && xhr.status < 300) {
          finish('ok');
          return;
        }
        finish({
          type: 'http',
          status: xhr.status,
          message: readMessage(xhr),
          retryAfterMs: parseRetryAfter(xhr.getResponseHeader('Retry-After'), Date.now()),
        });
      };
      xhr.onerror = (): void => finish({ type: 'network' });
      xhr.ontimeout = (): void => finish({ type: 'network' });
      xhr.onabort = (): void => {
        activeXhrRef.current = null;
        activeIdRef.current = null;
      };
      xhr.send(form);
    },
    [baseUrl, scheduleHistoryRefresh, updateItem],
  );

  useEffect(() => {
    if (linkState === 'dead' || activeIdRef.current !== null) return;
    const step = selectNext(items, Date.now());
    if (step.type === 'start') {
      const next = items.find((i) => i.id === step.id);
      if (next !== undefined) startUpload(next);
      return;
    }
    if (step.type === 'wait') {
      const timer = window.setTimeout(() => setTick((t) => t + 1), step.ms);
      return () => window.clearTimeout(timer);
    }
    return;
  }, [items, tick, linkState, startUpload]);

  // ---- picking -------------------------------------------------------------

  async function handlePick(e: ChangeEvent<HTMLInputElement>): Promise<void> {
    const chosen = Array.from(e.target.files ?? []);
    // Reset so picking the same file again still fires `change`.
    e.target.value = '';
    if (chosen.length === 0) return;
    // Copy the bytes now, while the phone is still sharing them, so a retry
    // never depends on a gallery handle that may have gone stale.
    const held = await Promise.all(chosen.map((file) => holdFileBytes(file)));
    if (!mountedRef.current) return;
    const added = chosen.map((picked, i) => {
      const file = held[i] ?? null;
      const it = createQueueItem(newId(), picked);
      if (file === null) return it.status === 'failed' ? it : markUnreadable(it);
      filesRef.current.set(it.id, file);
      if (it.kind === 'photo' && it.status !== 'failed') {
        thumbsRef.current.set(it.id, URL.createObjectURL(file));
      }
      return it;
    });
    setItems((prev) => [...prev, ...added]);
  }

  function retry(id: string): void {
    updateItem(id, manualRetry);
  }

  // ---- message -------------------------------------------------------------

  const saveMessage = useCallback(
    async (text: string, attempt = 0): Promise<void> => {
      if (baseUrl === null) return;
      setMessageState('saving');
      try {
        const res = await fetch(`${baseUrl}/message`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ batchId: batchIdRef.current, text }),
        });
        if (!mountedRef.current) return;
        if (res.ok) {
          setSavedMessage(text);
          setMessageState('saved');
          return;
        }
        if (res.status === 404 || res.status === 410) {
          setLinkState('dead');
          return;
        }
        if (res.status === 409 && attempt === 0) {
          batchIdRef.current = newId();
          await saveMessage(text, 1);
          return;
        }
        setMessageState('error');
      } catch {
        if (mountedRef.current) setMessageState('error');
      }
    },
    [baseUrl],
  );

  // After a batch change the saved text is cleared; re-save it silently.
  useEffect(() => {
    if (savedMessage === '' && message.trim() !== '' && messageState === 'saved') {
      void saveMessage(message);
    }
  }, [savedMessage, message, messageState, saveMessage]);

  const messageDirty = message !== savedMessage;

  // ---- render --------------------------------------------------------------

  if (linkState === 'dead') {
    return (
      <Shell>
        <div className="mt-8 flex flex-col items-center gap-4 rounded-2xl border bg-white px-6 py-10 text-center shadow-sm dark:bg-zinc-900">
          <Link2Off className="h-10 w-10 text-zinc-400" aria-hidden />
          <p className="text-base font-medium text-zinc-800 dark:text-zinc-100">
            {DEAD_LINK_MESSAGE}
          </p>
        </div>
        {summary.sent > 0 && (
          <p className="mt-4 text-center text-sm text-zinc-600 dark:text-zinc-400">
            {summary.text} before the link stopped working.
          </p>
        )}
      </Shell>
    );
  }

  const disabled = linkState !== 'live';

  return (
    <Shell>
      <div className="mt-5 flex flex-col gap-3">
        <PickButton
          icon={<Images className="h-7 w-7" aria-hidden />}
          label="Photos & videos"
          hint="From your gallery"
          tone="violet"
          disabled={disabled}
          onClick={() => photosInput.current?.click()}
        />
        <PickButton
          icon={<Camera className="h-7 w-7" aria-hidden />}
          label="Take a photo"
          hint="Use your camera"
          tone="blue"
          disabled={disabled}
          onClick={() => cameraInput.current?.click()}
        />
        <PickButton
          icon={<FileText className="h-7 w-7" aria-hidden />}
          label="Files & PDFs"
          hint="Menus, brochures, documents"
          tone="emerald"
          disabled={disabled}
          onClick={() => filesInput.current?.click()}
        />
        <input
          ref={photosInput}
          type="file"
          accept="image/*,video/*"
          multiple
          className="hidden"
          onChange={(e) => void handlePick(e)}
          tabIndex={-1}
          aria-hidden
        />
        <input
          ref={cameraInput}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={(e) => void handlePick(e)}
          tabIndex={-1}
          aria-hidden
        />
        <input
          ref={filesInput}
          type="file"
          accept=".pdf,application/pdf,image/*"
          multiple
          className="hidden"
          onChange={(e) => void handlePick(e)}
          tabIndex={-1}
          aria-hidden
        />
        <p className="text-center text-xs text-zinc-500 dark:text-zinc-400">
          Photos up to 25 MB · videos and PDFs up to 100 MB
        </p>
      </div>

      {items.length > 0 && (
        <section className="mt-6" aria-live="polite">
          <div className="flex items-baseline justify-between gap-3">
            <h2 className="text-sm font-semibold text-zinc-800 dark:text-zinc-100">Sending</h2>
            <span className="text-sm text-zinc-600 dark:text-zinc-400">{summary.text}</span>
          </div>
          <ul className="mt-2 flex flex-col gap-2">
            {items.map((it) => (
              <QueueRow
                key={it.id}
                item={it}
                thumb={thumbsRef.current.get(it.id) ?? null}
                onRetry={() => retry(it.id)}
              />
            ))}
          </ul>
          {!summary.busy && summary.sent > 0 && summary.failed === 0 && (
            <p className="mt-3 flex items-center gap-2 rounded-xl border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-300">
              <CheckCircle2 className="h-5 w-5 shrink-0" aria-hidden />
              All done. You can close this page when everything shows Sent ✓
            </p>
          )}
          {summary.busy && (
            <p className="mt-3 text-center text-xs text-zinc-500 dark:text-zinc-400">
              Please keep this page open until everything shows Sent ✓
            </p>
          )}
        </section>
      )}

      <section className="mt-6">
        <label
          htmlFor="client-upload-message"
          className="text-sm font-semibold text-zinc-800 dark:text-zinc-100"
        >
          Add a message for your agency (optional)
        </label>
        <textarea
          id="client-upload-message"
          rows={3}
          maxLength={MAX_MESSAGE_LENGTH}
          value={message}
          disabled={disabled}
          onChange={(e) => {
            setMessage(e.target.value);
            if (messageState !== 'saving') setMessageState('idle');
          }}
          onBlur={() => {
            if (messageDirty && messageState !== 'saving') void saveMessage(message);
          }}
          placeholder="For example: use the second photo for the Diwali post"
          className="mt-2 w-full rounded-xl border border-zinc-300 bg-white px-4 py-3 text-base text-zinc-900 placeholder:text-zinc-400 focus:border-violet-500 focus:outline-none focus:ring-2 focus:ring-violet-500/30 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100"
        />
        <div className="mt-2 flex items-center justify-between gap-3">
          <span className="text-sm" aria-live="polite">
            {messageState === 'saved' && !messageDirty && (
              <span className="text-emerald-700 dark:text-emerald-400">Message saved ✓</span>
            )}
            {messageState === 'saving' && (
              <span className="text-zinc-500 dark:text-zinc-400">Saving…</span>
            )}
            {messageState === 'error' && (
              <span className="text-red-700 dark:text-red-400">
                Couldn&apos;t save — please try again
              </span>
            )}
          </span>
          <span className="text-xs tabular-nums text-zinc-500 dark:text-zinc-400">
            {message.length}/{MAX_MESSAGE_LENGTH}
          </span>
        </div>
        <button
          type="button"
          disabled={disabled || !messageDirty || messageState === 'saving'}
          onClick={() => void saveMessage(message)}
          className="mt-3 min-h-14 w-full rounded-xl border border-zinc-300 bg-white px-4 text-base font-medium text-zinc-900 active:bg-zinc-100 disabled:opacity-50 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100 dark:active:bg-zinc-800"
        >
          Save message
        </button>
      </section>

      {sent.length > 0 && (
        <section className="mt-8 border-t border-zinc-200 pt-5 dark:border-zinc-800">
          <h2 className="text-sm font-semibold text-zinc-800 dark:text-zinc-100">Sent so far</h2>
          <ul className="mt-2 flex flex-col gap-1.5">
            {sent.map((f, i) => (
              <li
                key={`${f.originalName ?? 'file'}-${f.receivedAt}-${String(i)}`}
                className="flex items-baseline justify-between gap-3 text-sm text-zinc-600 dark:text-zinc-400"
              >
                <span className="min-w-0 flex-1 truncate">{f.originalName ?? 'File'}</span>
                <span className="shrink-0 text-xs">
                  {f.bytes !== null && `${formatBytes(f.bytes)} · `}
                  {new Date(f.receivedAt).toLocaleDateString(undefined, {
                    day: 'numeric',
                    month: 'short',
                  })}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </Shell>
  );
}

function Shell({ children }: { children: ReactNode }): ReactNode {
  return (
    <div className="min-h-[100dvh] bg-zinc-50 dark:bg-zinc-950">
      <div
        className="mx-auto flex min-h-[100dvh] max-w-md flex-col"
        style={{
          paddingLeft: 'max(16px, env(safe-area-inset-left))',
          paddingRight: 'max(16px, env(safe-area-inset-right))',
          paddingBottom: 'max(24px, env(safe-area-inset-bottom))',
        }}
      >
        <header
          className="-mx-4 bg-gradient-to-br from-violet-600 via-blue-600 to-emerald-600 px-5 pb-6 text-white sm:mx-0 sm:mt-4 sm:rounded-2xl"
          style={{ paddingTop: 'max(24px, env(safe-area-inset-top))' }}
        >
          <h1 className="text-xl font-semibold leading-snug">
            Send us your photos, videos or files
          </h1>
          <p className="mt-1 text-sm text-white/90">They go straight to your account manager.</p>
        </header>
        {children}
      </div>
    </div>
  );
}

const TONES = {
  violet: 'bg-violet-600 active:bg-violet-700',
  blue: 'bg-blue-600 active:bg-blue-700',
  emerald: 'bg-emerald-600 active:bg-emerald-700',
} as const;

function PickButton(props: {
  icon: ReactNode;
  label: string;
  hint: string;
  tone: keyof typeof TONES;
  disabled: boolean;
  onClick: () => void;
}): ReactNode {
  return (
    <button
      type="button"
      disabled={props.disabled}
      onClick={props.onClick}
      className={`flex min-h-16 w-full items-center gap-4 rounded-2xl px-5 py-3 text-left text-white shadow-sm disabled:opacity-60 ${TONES[props.tone]}`}
    >
      {props.icon}
      <span className="flex flex-col">
        <span className="text-lg font-semibold leading-tight">{props.label}</span>
        <span className="text-sm text-white/85">{props.hint}</span>
      </span>
    </button>
  );
}

function QueueRow(props: {
  item: QueueItem;
  thumb: string | null;
  onRetry: () => void;
}): ReactNode {
  const { item, thumb } = props;
  const [thumbBroken, setThumbBroken] = useState(false);
  const failed = item.status === 'failed';
  const tappable = failed && item.retryable;

  const icon =
    item.kind === 'video' ? (
      <Film className="h-6 w-6 text-zinc-500" aria-hidden />
    ) : item.kind === 'photo' ? (
      <Images className="h-6 w-6 text-zinc-500" aria-hidden />
    ) : (
      <FileText className="h-6 w-6 text-zinc-500" aria-hidden />
    );

  const content = (
    <>
      <span className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-zinc-100 dark:bg-zinc-800">
        {thumb !== null && !thumbBroken ? (
          <img
            src={thumb}
            alt=""
            className="h-full w-full object-cover"
            // HEIC does not decode in most browsers; fall back to the icon.
            onError={() => setThumbBroken(true)}
          />
        ) : (
          icon
        )}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium text-zinc-900 dark:text-zinc-100">
          {item.name}
        </span>
        <span className="block text-xs text-zinc-500 dark:text-zinc-400">
          {formatBytes(item.bytes)}
        </span>
        {item.status === 'uploading' && (
          <span
            className="mt-1.5 block h-1.5 w-full overflow-hidden rounded-full bg-zinc-200 dark:bg-zinc-800"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={item.progress}
            aria-label={`Sending ${item.name}`}
          >
            <span
              className="block h-full rounded-full bg-violet-600 transition-[width] duration-200"
              style={{ width: `${String(item.progress)}%` }}
            />
          </span>
        )}
        <span className="mt-1 block text-xs">
          <RowStatus item={item} />
        </span>
      </span>
      {tappable && <RotateCw className="h-5 w-5 shrink-0 text-red-600" aria-hidden />}
    </>
  );

  const base = 'flex min-h-14 w-full items-center gap-3 rounded-xl border px-3 py-2 text-left';
  const tone = failed
    ? 'border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-950/30'
    : 'border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-900';

  return (
    <li>
      {tappable ? (
        <button type="button" onClick={props.onRetry} className={`${base} ${tone}`}>
          {content}
        </button>
      ) : (
        <div className={`${base} ${tone}`}>{content}</div>
      )}
    </li>
  );
}

function RowStatus({ item }: { item: QueueItem }): ReactNode {
  switch (item.status) {
    case 'done':
      return <span className="font-medium text-emerald-700 dark:text-emerald-400">Sent ✓</span>;
    case 'uploading':
      return (
        <span className="text-zinc-600 dark:text-zinc-300">Sending {String(item.progress)}%</span>
      );
    case 'pending':
      return (
        <span className="text-zinc-500 dark:text-zinc-400">
          {item.retryAt !== null ? 'Waiting to retry…' : 'Waiting…'}
        </span>
      );
    case 'failed':
      return (
        <span className="flex items-start gap-1 text-red-700 dark:text-red-400">
          <AlertCircle className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden />
          <span>
            {item.retryable
              ? `Failed${item.reason !== null ? ` · ${item.reason}` : ' — tap to retry'}`
              : `Not sent${item.reason !== null ? ` · ${item.reason}` : ''}`}
          </span>
        </span>
      );
  }
}
